import {getSubscriptions, updateSubscription, createSubscription} from "../Services/VenueService.ts";
import React, {useEffect, useState, useCallback} from "react";
import Helper from "../helpers";
import LayoutWrapper from '../components/LayoutWrapper';
import { ArrowPathIcon, CurrencyPoundIcon, Cog6ToothIcon, CloudArrowDownIcon, WrenchScrewdriverIcon, EyeIcon, EyeSlashIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';

const formatCycleDisplay = (cycleType, cycleLength) => {
    const typeLabels = { 0: 'day', 1: 'week', 2: 'month', 3: 'quarter', 4: 'year' };
    const label = typeLabels[cycleType] || 'period';
    if (cycleLength && cycleLength > 1) {
        return `Every ${cycleLength} ${label}s`;
    }
    const singleLabels = { 0: 'Daily', 1: 'Weekly', 2: 'Monthly', 3: 'Quarterly', 4: 'Yearly' };
    return singleLabels[cycleType] || 'Custom';
};

const Subscriptions = () => {
    const [currentVenueKey, setCurrentVenueKey] = useState("");
    const [venueKeyOptions, setVenueKeyOptions] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [formData, setFormData] = useState({});

    // ── Create subscription modal ──────────────────────────────────────────
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createForm, setCreateForm] = useState({
        name: '', description: '', price: '', cycleType: 2,
        cycleLength: 1, tokensPerCycle: 0, runAnchorDate: ''
    });
    const [createError, setCreateError] = useState('');
    const [createSaving, setCreateSaving] = useState(false);

    const resetCreateForm = () => {
        setCreateForm({ name: '', description: '', price: '', cycleType: 2, cycleLength: 1, tokensPerCycle: 0, runAnchorDate: '' });
        setCreateError('');
    };

    const handleCreateSubscription = async () => {
        setCreateError('');
        if (!createForm.name.trim()) { setCreateError('Please enter a subscription name.'); return; }
        const price = parseFloat(createForm.price);
        if (isNaN(price) || price < 0) { setCreateError('Please enter a valid price.'); return; }
        if (price > 0 && price < 1) { setCreateError('Price must be at least £1.00 or £0 for free plans.'); return; }

        setCreateSaving(true);
        try {
            await createSubscription(
                currentVenueKey,
                createForm.name.trim(),
                price,
                parseInt(createForm.cycleType),
                parseInt(createForm.cycleLength) || 1,
                parseInt(createForm.tokensPerCycle) || 0,
                createForm.description.trim(),
                createForm.runAnchorDate || null
            );
            setShowCreateModal(false);
            resetCreateForm();
            await fetchSubscriptions();
        } catch (err) {
            setCreateError(err.message || 'Failed to create subscription.');
        } finally {
            setCreateSaving(false);
        }
    };

    const fetchSubscriptions = useCallback(async () => {
        if (currentVenueKey) {
            let subResultData = await getSubscriptions(currentVenueKey);

            if (subResultData && subResultData.items.length > 0) {
                setSubscriptions(subResultData.items);
                const initialFormData = {};
                subResultData.items.forEach(sub => {
                    initialFormData[sub.id] = {
                        tokensPerCycle: sub.tokensPerCycle || 0,
                        cycleType: sub.subscriptionCycleType ?? 1,
                        cycleCost: sub.subscriptionCycleCost || 0,
                        cycleLength: sub.subscriptionCycleWeeks || 1,
                        isImported: sub.isImported || false,
                        showOnSignup: sub.showOnSignup || false,
                        runAnchorDate: sub.runAnchorDate ? sub.runAnchorDate.slice(0, 10) : ''
                    };
                });
                setFormData(initialFormData);
            } else {
                setSubscriptions([]);
                setFormData({});
            }
        }
    }, [currentVenueKey]);

    useEffect(() => {
        const initialize = async () => {
            await Helper.getAdminVenues(setCurrentVenueKey, setVenueKeyOptions);
            await fetchSubscriptions();
        };
        initialize();
    }, [currentVenueKey, fetchSubscriptions]);

    const updateFormField = (subscriptionId, field, value) => {
        setFormData(prev => ({
            ...prev,
            [subscriptionId]: {
                ...prev[subscriptionId],
                [field]: value
            }
        }));
    };

    const handleUpdate = async (subscriptionId) => {
        const data = formData[subscriptionId];
        if (!data) return;

        try {
            await updateSubscription(
                currentVenueKey,
                subscriptionId,
                data.tokensPerCycle,
                data.cycleType,
                data.cycleCost,
                data.cycleLength,
                data.showOnSignup,
                data.runAnchorDate || null
            );
            alert('Subscription updated successfully');
        } catch (error) {
            console.error('Error updating subscription:', error);
            alert('Failed to update subscription');
        }
    };

    // Plans made for one client from a custom offer link are shown in their own section below
    const standardSubscriptions = subscriptions.filter(sub => !sub.isCustomerSubscription);
    const customSubscriptions = subscriptions.filter(sub => sub.isCustomerSubscription);

    return (
        <LayoutWrapper
            title="Manage Subscriptions"
            subtitle="Update subscription settings and pricing for your venue"
        >
            {/* Hidden venue selector */}
            {venueKeyOptions.length > 0 && (
                <select
                    className="hidden"
                    value={currentVenueKey}
                    onChange={(e) => setCurrentVenueKey(e.target.value)}
                >
                    {venueKeyOptions.map((venueKey) => (
                        <option key={venueKey} value={venueKey}>{venueKey}</option>
                    ))}
                </select>
            )}

            {/* Header with Refresh Button */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                    <Cog6ToothIcon className="h-6 w-6 text-emerald-400" />
                    <div>
                        <h2 className="text-xl font-semibold text-white">Subscription Settings</h2>
                        <p className="text-gray-400 text-sm">Configure tokens, pricing, and billing cycles</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => { resetCreateForm(); setShowCreateModal(true); }}
                        className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors duration-200 flex items-center space-x-2"
                    >
                        <PlusIcon className="h-4 w-4" />
                        <span>Create Subscription</span>
                    </button>
                    <button
                        onClick={fetchSubscriptions}
                        className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors duration-200 flex items-center space-x-2"
                    >
                        <ArrowPathIcon className="h-4 w-4" />
                        <span>Refresh</span>
                    </button>
                </div>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-4 text-sm">
                <div className="flex items-center space-x-2">
                    <CloudArrowDownIcon className="h-4 w-4 text-blue-400" />
                    <span className="text-gray-400">GoCardless Imported &mdash; pricing &amp; billing cycle are read-only</span>
                </div>
                <div className="flex items-center space-x-2">
                    <WrenchScrewdriverIcon className="h-4 w-4 text-emerald-400" />
                    <span className="text-gray-400">Platform Managed &mdash; fully editable</span>
                </div>
                <div className="flex items-center space-x-2">
                    <EyeIcon className="h-4 w-4 text-amber-400" />
                    <span className="text-gray-400">Show on Signup &mdash; visible to new users on the sign-up page</span>
                </div>
            </div>

            {/* Subscriptions Table */}
            <div className="backdrop-blur-sm bg-gray-900/20 border border-gray-700/30 rounded-xl overflow-hidden">
                {standardSubscriptions.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-800/50 border-b border-gray-700/30">
                                <tr>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Source</th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Name</th>
                                    <th className="px-4 py-4 text-center text-sm font-medium text-gray-300">Signup</th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Tokens per Cycle</th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Billing Cycle</th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Every</th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Cost per Cycle</th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-300" title="A date from your real direct debit run. New members are lined up to it.">Run date</th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700/30">
                                {standardSubscriptions.map(subscription => {
                                    const data = formData[subscription.id] || {};
                                    const imported = data.isImported;

                                    return (
                                        <tr key={subscription.id} className="hover:bg-gray-800/30 transition-colors duration-200">
                                            {/* Source badge */}
                                            <td className="px-6 py-4">
                                                {imported ? (
                                                    <span className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-900/40 text-blue-300 border border-blue-700/40">
                                                        <CloudArrowDownIcon className="h-3.5 w-3.5" />
                                                        <span>GoCardless</span>
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-900/40 text-emerald-300 border border-emerald-700/40">
                                                        <WrenchScrewdriverIcon className="h-3.5 w-3.5" />
                                                        <span>Platform</span>
                                                    </span>
                                                )}
                                            </td>

                                            {/* Name */}
                                            <td className="px-6 py-4 text-sm text-white font-medium">{subscription.name}</td>

                                            {/* Show on Signup toggle */}
                                            <td className="px-4 py-4 text-center">
                                                <button
                                                    onClick={() => updateFormField(subscription.id, 'showOnSignup', !data.showOnSignup)}
                                                    className={`inline-flex items-center justify-center w-10 h-10 rounded-lg transition-colors duration-200 ${
                                                        data.showOnSignup
                                                            ? 'bg-amber-600/30 text-amber-300 border border-amber-500/40 hover:bg-amber-600/50'
                                                            : 'bg-gray-800 text-gray-500 border border-gray-600 hover:bg-gray-700 hover:text-gray-400'
                                                    }`}
                                                    title={data.showOnSignup ? 'Visible on signup page — click to hide' : 'Hidden from signup page — click to show'}
                                                >
                                                    {data.showOnSignup
                                                        ? <EyeIcon className="h-5 w-5" />
                                                        : <EyeSlashIcon className="h-5 w-5" />
                                                    }
                                                </button>
                                            </td>

                                            {/* Tokens — always editable */}
                                            <td className="px-6 py-4">
                                                <input
                                                    type="number"
                                                    value={data.tokensPerCycle || 0}
                                                    onChange={(e) => updateFormField(subscription.id, 'tokensPerCycle', parseInt(e.target.value) || 0)}
                                                    className="w-24 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
                                                    placeholder="Tokens"
                                                />
                                            </td>

                                            {/* Cycle Type */}
                                            <td className="px-6 py-4">
                                                {imported ? (
                                                    <span className="text-sm text-gray-300">
                                                        {formatCycleDisplay(data.cycleType, data.cycleLength)}
                                                    </span>
                                                ) : (
                                                    <select
                                                        value={data.cycleType ?? 1}
                                                        onChange={(e) => updateFormField(subscription.id, 'cycleType', parseInt(e.target.value))}
                                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
                                                    >
                                                        {data.cycleType === 0 && <option value={0}>Daily (not supported, please change)</option>}
                                                        <option value={1}>Weekly</option>
                                                        <option value={2}>Monthly</option>
                                                        <option value={3}>Quarterly</option>
                                                        <option value={4}>Yearly</option>
                                                    </select>
                                                )}
                                            </td>

                                            {/* Cycle Length (e.g. every 4 weeks) */}
                                            <td className="px-6 py-4">
                                                {imported ? (
                                                    <span className="text-sm text-gray-300">
                                                        {data.cycleLength || 1}
                                                    </span>
                                                ) : (
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={data.cycleLength || 1}
                                                        onChange={(e) => updateFormField(subscription.id, 'cycleLength', parseInt(e.target.value) || 1)}
                                                        className="w-20 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
                                                        placeholder="1"
                                                    />
                                                )}
                                            </td>

                                            {/* Cost per Cycle */}
                                            <td className="px-6 py-4">
                                                {imported ? (
                                                    <span className="text-sm text-white font-medium">
                                                        £{(data.cycleCost || 0).toFixed(2)}
                                                    </span>
                                                ) : (
                                                    <div className="relative">
                                                        <CurrencyPoundIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            value={data.cycleCost || 0}
                                                            onChange={(e) => updateFormField(subscription.id, 'cycleCost', parseFloat(e.target.value) || 0)}
                                                            className="w-28 pl-10 pr-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                )}
                                            </td>

                                            {/* Run date: first direct debit lines up with this date plus whole cycles */}
                                            <td className="px-6 py-4">
                                                {imported ? (
                                                    <span className="text-sm text-gray-500">Set in GoCardless</span>
                                                ) : (
                                                    <input
                                                        type="date"
                                                        value={data.runAnchorDate || ''}
                                                        onChange={(e) => updateFormField(subscription.id, 'runAnchorDate', e.target.value)}
                                                        className="px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
                                                    />
                                                )}
                                            </td>

                                            {/* Actions */}
                                            <td className="px-6 py-4">
                                                <button
                                                    onClick={() => handleUpdate(subscription.id)}
                                                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors duration-200"
                                                >
                                                    Update
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="p-12 text-center">
                        <Cog6ToothIcon className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-white mb-2">No Subscriptions Found</h3>
                        <p className="text-gray-400 mb-6">No subscription plans are currently configured for this venue.</p>
                        <button
                            onClick={() => { resetCreateForm(); setShowCreateModal(true); }}
                            className="inline-flex items-center gap-2 px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors"
                        >
                            <PlusIcon className="h-4 w-4" />
                            Create Your First Subscription
                        </button>
                    </div>
                )}
            </div>

            {/* Custom plans: one per client, created from a custom offer link. Read only. */}
            {customSubscriptions.length > 0 && (
                <div className="mt-8">
                    <div className="mb-3">
                        <h2 className="text-lg font-semibold text-white">Custom plans</h2>
                        <p className="text-gray-400 text-sm">
                            Created for one client from a custom offer link. Their price is already set up with GoCardless, so these are shown here for reference only.
                        </p>
                    </div>
                    <div className="backdrop-blur-sm bg-gray-900/20 border border-gray-700/30 rounded-xl overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-800/50 border-b border-gray-700/30">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-300">Name</th>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-300">Price</th>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-300">Repeats</th>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-300">Tokens</th>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-300">Run date</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-700/30">
                                    {customSubscriptions.map(sub => (
                                        <tr key={sub.id}>
                                            <td className="px-6 py-3 text-sm text-white font-medium">{sub.name}</td>
                                            <td className="px-6 py-3 text-sm text-white">£{(sub.subscriptionCycleCost || 0).toFixed(2)}</td>
                                            <td className="px-6 py-3 text-sm text-gray-300">{formatCycleDisplay(sub.subscriptionCycleType, sub.subscriptionCycleWeeks)}</td>
                                            <td className="px-6 py-3 text-sm text-gray-300">{sub.tokensPerCycle}</td>
                                            <td className="px-6 py-3 text-sm text-gray-300">
                                                {sub.runAnchorDate ? new Date(sub.runAnchorDate).toLocaleDateString('en-GB') : 'Not set'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Create Subscription Modal ──────────────────────────────── */}
            {showCreateModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg shadow-2xl">
                        {/* Modal header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-emerald-600/20 rounded-lg">
                                    <WrenchScrewdriverIcon className="h-5 w-5 text-emerald-400" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white">Create Subscription</h3>
                                    <p className="text-gray-400 text-xs">Platform-managed plan for your venue</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowCreateModal(false)}
                                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                            >
                                <XMarkIcon className="h-5 w-5" />
                            </button>
                        </div>

                        {/* Modal body */}
                        <div className="px-6 py-5 space-y-4">
                            {/* Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Subscription Name <span className="text-red-400">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={createForm.name}
                                    onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
                                    placeholder="e.g. Monthly Membership"
                                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                />
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
                                <input
                                    type="text"
                                    value={createForm.description}
                                    onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))}
                                    placeholder="Optional description for members"
                                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                />
                            </div>

                            {/* Price */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Price per Cycle (£) <span className="text-red-400">*</span>
                                </label>
                                <div className="relative">
                                    <CurrencyPoundIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={createForm.price}
                                        onChange={e => setCreateForm(f => ({ ...f, price: e.target.value }))}
                                        placeholder="0.00"
                                        className="w-full pl-10 pr-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                    />
                                </div>
                            </div>

                            {/* Billing cycle row */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">Billing Cycle</label>
                                    <select
                                        value={createForm.cycleType}
                                        onChange={e => setCreateForm(f => ({ ...f, cycleType: parseInt(e.target.value) }))}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                    >
                                        <option value={1}>Weekly</option>
                                        <option value={2}>Monthly</option>
                                        <option value={3}>Quarterly</option>
                                        <option value={4}>Yearly</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">Every (N periods)</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={createForm.cycleLength}
                                        onChange={e => setCreateForm(f => ({ ...f, cycleLength: e.target.value }))}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                    />
                                </div>
                            </div>

                            {/* Run date */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">Run date (optional)</label>
                                <input
                                    type="date"
                                    value={createForm.runAnchorDate}
                                    onChange={e => setCreateForm(f => ({ ...f, runAnchorDate: e.target.value }))}
                                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    A date from your normal direct debit run, for example one of your 4 weekly dates. New members are lined up to it. Leave empty to let GoCardless choose.
                                </p>
                            </div>

                            {/* Tokens */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">Tokens per Cycle</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={createForm.tokensPerCycle}
                                    onChange={e => setCreateForm(f => ({ ...f, tokensPerCycle: e.target.value }))}
                                    placeholder="0"
                                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                />
                            </div>

                            {createError && (
                                <div className="px-4 py-3 bg-red-900/30 border border-red-700/40 rounded-lg text-sm text-red-300">
                                    {createError}
                                </div>
                            )}
                        </div>

                        {/* Modal footer */}
                        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-700">
                            <button
                                onClick={() => setShowCreateModal(false)}
                                className="px-4 py-2 text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600 rounded-lg text-sm font-medium transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateSubscription}
                                disabled={createSaving}
                                className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                            >
                                {createSaving ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <PlusIcon className="h-4 w-4" />
                                        Create Subscription
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </LayoutWrapper>
    );
}

export default Subscriptions;
